import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { normalizeCoordinates, validateCoordinates, debugCoordinates } from '../utils/locationUtils';

// Log to help debug
console.log('[useSublocations] Using locationUtils for coordinate handling');

export function useSublocations(siteId) {
  const [sublocations, setSublocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSublocations = useCallback(async () => {
    if (!siteId) return;
    
    setLoading(true);
    try {
      const q = query(
        collection(db, 'site_sublocations'),
        where('siteId', '==', siteId)
      );
      const querySnapshot = await getDocs(q);
      
      const sublocationData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Ensure all coordinates are properly normalized
      const normalizedSublocations = sublocationData.map(sublocation => {
        if (sublocation.coordinates) {
          const normalizedCoords = normalizeCoordinates(sublocation.coordinates);
          const validatedCoords = validateCoordinates(normalizedCoords);
          
          // Log coordinate transformation for debugging
          debugCoordinates(`useSublocations(${sublocation.id})`, 
            sublocation.coordinates,
            normalizedCoords,
            validatedCoords
          );
          
          return {
            ...sublocation,
            coordinates: validatedCoords
          };
        }
        console.warn(`[useSublocations] Sublocation ${sublocation.id} has no coordinates`);
        return sublocation;
      });
      
      setSublocations(normalizedSublocations);
      setError(null);
    } catch (err) {
      console.error('Error fetching sublocations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchSublocations();
  }, [fetchSublocations]);

  const createSublocation = async (data) => {
    if (!siteId) throw new Error('Site ID is required');
    
    try {
      // Normalize and validate coordinates
      if (data.coordinates) {
        const normalizedCoords = normalizeCoordinates(data.coordinates);
        const validatedCoords = validateCoordinates(normalizedCoords);
        
        debugCoordinates('createSublocation', data.coordinates, normalizedCoords, validatedCoords);
        
        if (!validatedCoords) {
          console.error('[useSublocations] Invalid coordinates provided:', data.coordinates);
          throw new Error('Invalid coordinates provided');
        }
        
        // Ensure coordinates are stored as numbers
        data.coordinates = {
          lat: Number(validatedCoords.lat),
          lng: Number(validatedCoords.lng)
        };
        
        console.log('[useSublocations] Creating sublocation with coordinates:', data.coordinates);
      } else {
        console.warn('[useSublocations] Creating sublocation without coordinates');
      }
      
      const docRef = await addDoc(collection(db, 'site_sublocations'), {
        ...data,
        siteId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Add the new sublocation to the state
      setSublocations(prev => [...prev, {
        id: docRef.id,
        ...data,
        siteId,
        createdAt: new Date(),
        updatedAt: new Date()
      }]);

      return docRef.id;
    } catch (err) {
      console.error('Error creating sublocation:', err);
      throw err;
    }
  };

  const updateSublocation = async (sublocationId, data) => {
    try {
      // Normalize and validate coordinates if provided
      if (data.coordinates) {
        const normalizedCoords = normalizeCoordinates(data.coordinates);
        const validatedCoords = validateCoordinates(normalizedCoords);
        
        debugCoordinates('updateSublocation', data.coordinates, normalizedCoords, validatedCoords);
        
        if (!validatedCoords) {
          console.error('[useSublocations] Invalid coordinates provided for update:', data.coordinates);
          throw new Error('Invalid coordinates provided');
        }
        
        // Ensure coordinates are stored as numbers
        data.coordinates = {
          lat: Number(validatedCoords.lat),
          lng: Number(validatedCoords.lng)
        };
        
        console.log('[useSublocations] Updating sublocation with coordinates:', data.coordinates);
      }
      
      const sublocationRef = doc(db, 'site_sublocations', sublocationId);
      await updateDoc(sublocationRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      
      // Update the sublocation in state
      setSublocations(prev =>
        prev.map(loc =>
          loc.id === sublocationId
            ? { ...loc, ...data, updatedAt: new Date() }
            : loc
        )
      );
    } catch (err) {
      console.error('Error updating sublocation:', err);
      throw err;
    }
  };

  const deleteSublocation = async (sublocationId) => {
    try {
      await deleteDoc(doc(db, 'site_sublocations', sublocationId));
      
      // Remove the sublocation from state
      setSublocations(prev => prev.filter(loc => loc.id !== sublocationId));
    } catch (err) {
      console.error('Error deleting sublocation:', err);
      throw err;
    }
  };

  return {
    sublocations,
    loading,
    error,
    fetchSublocations,
    createSublocation,
    updateSublocation,
    deleteSublocation
  };
}
